import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Package, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  ArrowLeft, 
  ShoppingBag, 
  Truck, 
  MapPin, 
  CreditCard, 
  X, 
  Receipt,
  LogIn,
  BadgeCheck
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import apiService from '../../components/services/apiService';
import { formatVND, formatDateTime } from '../../utils/formatters';

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL'); // ALL, PENDING, SHIPPING, COMPLETED, CANCELLED
  
  // State quản lý Modal xem chi tiết đơn hàng
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { id: routeOrderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    if (user && user.id) {
    fetchOrders();
    } else {
      setLoading(false);
    }
  }, [user]);

  // Tự động mở Modal nếu người dùng truy cập trực tiếp qua Route /orders/:id
  useEffect(() => {
    if (routeOrderId && orders.length > 0) {
      const matched = orders.find(
        (o) => String(o.id) === String(routeOrderId) || String(o.orderCode) === String(routeOrderId)
      );
      if (matched) {
        setSelectedOrder(matched);
        setIsDetailOpen(true);
      }
    }
  }, [routeOrderId, orders]);

const fetchOrders = async () => {
    if (!user || !user.id) return;
    try {
      setLoading(true);
      const res = await apiService.get(`/orders/${user.id}`);
      const dataOrders = res.data?.data || res.data;
      
      const ordersArray = Array.isArray(dataOrders) ? dataOrders : [];
      
      // Sắp xếp đơn hàng mới nhất lên đầu tiên dựa vào thời gian tạo (createdAt hoặc orderDate)
      ordersArray.sort((a, b) => {
        const timeA = new Date(b.createdAt || b.orderDate || 0).getTime();
        const timeB = new Date(a.createdAt || a.orderDate || 0).getTime();
        return timeA - timeB; // Đơn mới nhất (thời gian lớn hơn) sẽ đứng trước
      });

      setOrders(ordersArray);
    } catch (error) {
      console.error("Lỗi tải danh sách đơn hàng:", error);
      const errMsg = error.response?.data?.message || "";
      if (errMsg.includes("Không tìm thấy đơn hàng") || error.response?.status === 404) {
        setOrders([]);
      } else {
        showToast("Không thể tải danh sách đơn hàng", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // Mở modal và đồng bộ với URL /orders/:id
  const handleOpenDetail = (order) => {
    setSelectedOrder(order);
    setIsDetailOpen(true);
    navigate(`/orders/${order.id || order.orderCode}`);
  };

  // Đóng modal và trả về URL gốc /orders
  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setSelectedOrder(null);
    if (routeOrderId) {
      navigate('/orders');
    }
  };

  // Hủy đơn hàng (khi đơn đang ở trạng thái chờ)
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm("Bạn có chắc chắn muốn hủy đơn hàng này?")) return;
    try {
      await apiService.put(`/orders/${orderId}/cancel`, { status: 'CANCELLED' });
      
      const targetOrder = orders.find(o => o.id === orderId || o.orderId === orderId);
      const code = targetOrder?.orderCode ? `#${targetOrder.orderCode}` : `#${orderId}`;

      showToast({
        title: "Hủy đơn hàng thành công!",
        message: `Đơn hàng ${code} đã được hủy thành công.`,
        description: "Đơn hàng đã hủy sẽ được lưu tạm thời và hệ thống sẽ tự động xóa vĩnh viễn khỏi dữ liệu sau nửa ngày (12 giờ).",
        badge: "Tự động xóa sau 12h",
        extraNote: "Dữ liệu đơn hàng sẽ tự động được dọn dẹp khỏi DB sau 12 giờ.",
        type: "cancel",
        duration: 6000,
      });

      setOrders(prev => prev.map(o => ((o.id === orderId || o.orderId === orderId) ? { ...o, status: 'CANCELLED' } : o)));
      if (selectedOrder && (selectedOrder.id === orderId || selectedOrder.orderId === orderId)) {
        setSelectedOrder(prev => ({ ...prev, status: 'CANCELLED' }));
      }
    } catch (error) {
      console.error("Lỗi khi hủy đơn hàng:", error);
      const msg = error.response?.data?.message || "Không thể hủy đơn hàng lúc này";
      showToast(msg, "error");
    }
  };

  // Kiểm tra đơn hàng có được phép hủy hay không (hỗ trợ cả PENDING và PROCESSING)
  const canCancelOrder = (order) => {
    if (!order) return false;
    const s = String(order.status || '').toUpperCase();
    return (
      s.includes('PENDING') || 
      s.includes('PROCESSING') || 
      s.includes('CHỜ') || 
      s.includes('XÁC NHẬN') || 
      s.includes('XỬ LÝ')
    );
  };

  // Hàm helper dịch trạng thái đơn hàng sang tiếng Việt & gắn màu sắc
  const getStatusBadge = (status, paymentStatus, paymentMethod) => {
    const s = String(status || "").toUpperCase();
    const isPaid = String(paymentStatus || "").toLowerCase() === 'paid';
    const isVnPay = String(paymentMethod || "").toUpperCase() === 'VNPAY';

    switch (s) {
      case 'PENDING':
      case 'CHỜ XỬ LÝ':
      case 'CHỜ XÁC NHẬN':
      case 'PROCESSING':
      case 'ĐANG XỬ LÝ':
        return (
          <span className="bg-amber-50 text-amber-600 border border-amber-200 px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> {(isPaid || isVnPay) ? 'Đang xác nhận đơn' : 'Chờ xác nhận'}
          </span>
        );
      case 'CONFIRMED':
      case 'ĐÃ XÁC NHẬN':
        return (
          <span className="bg-violet-50 text-violet-600 border border-violet-200 px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
            <BadgeCheck className="w-3.5 h-3.5" /> Đã xác nhận
          </span>
        );
      case 'SHIPPING':
      case 'ĐANG GIAO':
        return (
          <span className="bg-blue-50 text-blue-600 border border-blue-200 px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
            <Truck className="w-3.5 h-3.5" /> Đang giao hàng
          </span>
        );
      case 'COMPLETED':
      case 'HOÀN THÀNH':
        return (
          <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Hoàn thành
          </span>
        );
      case 'CANCELLED':
      case 'ĐÃ HỦY':
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="bg-rose-50 text-rose-600 border border-rose-200 px-3 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
              <XCircle className="w-3.5 h-3.5" /> Đã hủy
            </span>
            <span className="bg-amber-50/80 text-amber-700 border border-amber-200/80 px-2 py-0.5 rounded-full text-[10px] font-semibold flex items-center gap-1" title="Đơn hàng sẽ tự động xóa sạch khỏi hệ thống sau 12 giờ">
              <Clock className="w-3 h-3 text-amber-500" /> Tự xóa sau 12h
            </span>
          </div>
        );
      default:
        return (
          <span className="bg-slate-100 text-slate-600 border border-slate-200 px-3 py-1 rounded-full text-[11px] font-bold">
            {status || 'Đang xác nhận đơn'}
          </span>
        );
    }
  };

  // Lọc đơn hàng theo tab
  const filteredOrders = orders.filter(order => {
    if (filterStatus === 'ALL') return true;
    const s = String(order.status || "").toUpperCase();
    return s.includes(filterStatus);
  });

  // Nếu người dùng chưa đăng nhập
  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center space-y-4">
        <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto shadow-sm">
          <LogIn className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-extrabold text-slate-900">Vui lòng đăng nhập</h2>
        <p className="text-xs text-slate-500 leading-relaxed">
          Bạn cần đăng nhập tài khoản để theo dõi lịch sử đơn hàng và cập nhật tình trạng giao nhận.
        </p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-md cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay về trang chủ
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-24 text-center text-slate-400 text-xs font-semibold">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        Đang tải danh sách đơn hàng...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Nút quay lại trang chủ */}
      <div>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white hover:bg-slate-50 border border-slate-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay lại trang chủ
        </button>
      </div>

      {/* Tiêu đề trang */}
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Đơn Hàng Của Tôi</h1>
        <p className="text-xs text-slate-500 mt-1">
          Theo dõi lịch sử mua sắm và trạng thái vận chuyển các đơn hàng từ Nhật Bản
        </p>
      </div>

      {/* Các Tab lọc trạng thái */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-100">
        {[
          { key: 'ALL', label: 'Tất cả đơn' },
          { key: 'PENDING', label: 'Chờ xác nhận' },
          { key: 'PROCESSING', label: 'Đang xác nhận' },
          { key: 'CONFIRMED', label: 'Đã xác nhận' },
          { key: 'SHIPPING', label: 'Đang giao' },
          { key: 'COMPLETED', label: 'Hoàn thành' },
          { key: 'CANCELLED', label: 'Đã hủy' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilterStatus(tab.key)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer ${
              filterStatus === tab.key
                ? 'bg-indigo-600 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Danh sách đơn hàng */}
      <div className="space-y-4">
        {filteredOrders.length > 0 ? (
          filteredOrders.map((order) => (
            <div 
              key={order.id || order.orderId} 
              className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm hover:border-slate-300 transition space-y-4"
            >
              {/* Header của từng đơn hàng */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-bold">
                    <Package className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-slate-900">
                      Mã đơn: #{order.orderCode || order.id || order.orderId}
                    </span>
                    <p className="text-[11px] text-slate-400">
                      Ngày đặt: {formatDateTime(order.createdAt || order.orderDate)}
                    </p>
                  </div>
                </div>
                <div>
                  {getStatusBadge(order.status, order.paymentStatus, order.paymentMethod)}
                </div>
              </div>

              {/* Danh sách sản phẩm trong đơn hàng */}
              <div className="divide-y divide-slate-100">
                {(order.orderItems || order.items || []).map((item, index) => (
                  <div key={index} className="py-3 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0 border border-slate-200">
                        {item.image || item.images || item.thumbnail ? (
                          <img 
                            src={item.image || item.images || item.thumbnail} 
                            alt={item.productName || item.name} 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ShoppingBag className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-slate-800 line-clamp-1">
                          {item.productName || item.name}
                        </h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">Số lượng: x{item.quantity}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className="text-xs font-extrabold text-indigo-600">
                        {formatVND(item.price)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer đơn hàng: Tổng tiền & Nút thao tác */}
              <div className="pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="text-xs text-slate-500">
                  Tổng thanh toán: <span className="text-sm font-extrabold text-slate-900 ml-1">
                    {formatVND(order.finalAmount || order.totalAmount || order.totalPrice)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {canCancelOrder(order) && (
                    <button 
                      onClick={() => handleCancelOrder(order.id || order.orderId)}
                      className="px-3.5 py-2 text-rose-600 hover:bg-rose-50 border border-rose-200 font-bold rounded-xl text-xs transition cursor-pointer"
                    >
                      Hủy đơn
                    </button>
                  )}
                  <button 
                    onClick={() => handleOpenDetail(order)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
                  >
                    Xem chi tiết
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-3xl space-y-3">
            <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-full flex items-center justify-center mx-auto">
              <ShoppingBag className="w-6 h-6" />
            </div>
            <p className="text-xs font-bold text-slate-600">Không tìm thấy đơn hàng nào</p>
            <p className="text-[11px] text-slate-400">Bạn chưa có đơn hàng nào trong trạng thái này.</p>
          </div>
        )}
      </div>

      {/* --- MODAL XEM CHI TIẾT ĐƠN HÀNG TRÊN TRANG --- */}
      {isDetailOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-6 shadow-2xl relative">
            
            {/* Header Modal */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-bold">
                  <Receipt className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">
                    Chi tiết đơn hàng #{selectedOrder.orderCode || selectedOrder.id}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Thời gian đặt: {formatDateTime(selectedOrder.createdAt || selectedOrder.orderDate)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseDetail}
                className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition cursor-pointer"
                title="Đóng modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Trạng thái đơn & Thanh toán */}
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
              <div className="space-y-1">
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">
                  Trạng thái đơn hàng
                </span>
                <div>{getStatusBadge(selectedOrder.status, selectedOrder.paymentStatus, selectedOrder.paymentMethod)}</div>
              </div>
              <div className="space-y-1 text-right">
                <span className="text-[11px] text-slate-400 font-bold block uppercase tracking-wider">
                  Phương thức thanh toán
                </span>
                <div className="flex items-center gap-1.5 justify-end">
                  <CreditCard className="w-3.5 h-3.5 text-indigo-600" />
                  <span className="text-xs font-bold text-slate-800">
                    {selectedOrder.paymentMethod || 'COD'}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    selectedOrder.paymentStatus === 'paid' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedOrder.paymentStatus === 'paid' ? 'Đã thanh toán' : 'Chưa thanh toán'}
                  </span>
                </div>
              </div>
            </div>

            {/* Địa chỉ giao hàng */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1 text-xs">
              <span className="text-slate-400 font-bold flex items-center gap-1.5 uppercase text-[11px] tracking-wider">
                <MapPin className="w-3.5 h-3.5 text-indigo-600" /> Địa chỉ nhận hàng
              </span>
              <p className="text-slate-800 font-medium pl-5 leading-relaxed">
                {selectedOrder.shippingAaddress || selectedOrder.shippingAddress || 'Địa chỉ mặc định của tài khoản'}
              </p>
            </div>

            {/* Danh sách sản phẩm trong đơn */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Sản phẩm đã đặt ({(selectedOrder.orderItems || selectedOrder.items || []).length})
              </h4>
              <div className="border border-slate-100 rounded-2xl divide-y divide-slate-100 overflow-hidden">
                {(selectedOrder.orderItems || selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-xl overflow-hidden flex-shrink-0 border border-slate-200">
                        {item.image || item.images || item.thumbnail ? (
                          <img 
                            src={item.image || item.images || item.thumbnail} 
                            alt={item.productName || item.name} 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <ShoppingBag className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="text-xs font-bold text-slate-900 line-clamp-1">
                          {item.productName || item.name}
                        </h5>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Số lượng: x{item.quantity} {item.color && item.color !== 'default' ? `• Màu: ${item.color}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs font-extrabold text-indigo-600">
                        {formatVND(item.price)}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-[10px] text-slate-400">
                          Tổng: {formatVND((item.price || 0) * (item.quantity || 1))}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tổng kết tiền đơn hàng */}
            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-2 text-xs">
              <div className="flex justify-between text-slate-500">
                <span>Tạm tính tiền hàng:</span>
                <span className="font-semibold text-slate-800">
                  {formatVND(selectedOrder.totalAmount || selectedOrder.totalPrice)}
                </span>
              </div>
              <div className="flex justify-between text-slate-500">
                <span>Phí vận chuyển:</span>
                <span className="font-semibold text-slate-800">
                  {formatVND(selectedOrder.shippingFee || 0)}
                </span>
              </div>
              <div className="flex justify-between text-sm font-extrabold text-slate-900 pt-2 border-t border-slate-200">
                <span>Tổng tiền thanh toán:</span>
                <span className="text-indigo-600 text-base">
                  {formatVND(selectedOrder.finalAmount || selectedOrder.totalAmount || selectedOrder.totalPrice)}
                </span>
              </div>
            </div>

            {/* Nút hành động */}
            <div className="flex items-center justify-between pt-2">
              {canCancelOrder(selectedOrder) ? (
                <button
                  type="button"
                  onClick={() => handleCancelOrder(selectedOrder.id || selectedOrder.orderId)}
                  className="px-4 py-2.5 text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Hủy đơn hàng
                </button>
              ) : <div></div>}
              <button
                type="button"
                onClick={handleCloseDetail}
                className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition cursor-pointer shadow-sm"
              >
                Đóng
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}